import { useState } from 'react'
import getBenchmarkStrength from '../../helpers/dataHelpers/getBenchmarkStrength'
import Modal from '../common/Modal'
import StrengthExplanation from './StrengthExplanation'

// The MSI benchmark counts fill-sessions of questionnaires; other benchmark
// variants (participatieladder) pass their own label ('Medewerkers').
export const DEFAULT_RESPONDENT_LABEL = 'Ingevulde vragenlijsten'

/**
 * Top stats row for the benchmark detail page:
 *   Benchmark average | Organisations | Respondents | Strength
 *
 * Reads counts + respondentsPerCompany straight from the msiBenchmark response
 * and computes strength client-side (same helper the pill uses).
 *
 * @param {object} props
 * @param {object} props.result - msiBenchmark-shaped response
 * @param {string} [props.unit] - '%' (MSI effects, default) or a word like 'treden' (participatieladder)
 * @param {string} [props.respondentLabel] - Label for the respondent count (default 'Ingevulde vragenlijsten')
 * @param {string|null} [props.respondentSub] - Optional sub-line under the respondent count
 */
const BenchmarkHeader = ({ result, unit = '%', respondentLabel = DEFAULT_RESPONDENT_LABEL, respondentSub = null }) => {
    const [modalOpen, setModalOpen] = useState(false)

    // The count is unique fill-sessions, not persons — someone measured at two
    // meetmomenten counts twice. The label 'Ingevulde vragenlijsten' carries
    // that meaning on its own; no default caveat sub-line (user, 2026-07-13).

    const strength = getBenchmarkStrength({
        companyCount: result.companyCount,
        respondentCount: result.respondentCount,
        respondentsPerCompany: result.respondentsPerCompany || [],
    })

    const growth = result.benchmarkGrowth
    const growthLabel = growth != null
        ? `${growth > 0 ? '+' : ''}${growth}${unit === '%' ? '%' : ` ${unit}`}`
        : '—'

    return (
        <>
            <div className='bd-header-stats'>
                <div className='bd-stat'>
                    <span className='bd-stat-label'>Benchmark groei</span>
                    <span className={`bd-stat-value bd-stat-value--large ${growth != null && growth >= 0 ? 'bd-stat-value--positive' : 'bd-stat-value--negative'}`}>
                        {growthLabel}
                    </span>
                </div>

                <div className='bd-stat'>
                    <span className='bd-stat-label'>Organisaties</span>
                    <span className='bd-stat-value'>{result.companyCount}</span>
                </div>

                <div className='bd-stat'>
                    <span className='bd-stat-label'>{respondentLabel}</span>
                    <span className='bd-stat-value'>{result.respondentCount}</span>
                    {respondentSub && <span className='bd-stat-sub'>{respondentSub}</span>}
                </div>

                <div className='bd-stat bd-stat--clickable' onClick={() => setModalOpen(true)}>
                    <span className='bd-stat-label'>Sterkte</span>
                    <span className='bd-stat-value'>{strength.label}</span>
                    <span className='bd-stat-sub'>{strength.finalScore + 1}/4</span>
                </div>
            </div>

            <Modal
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                confirm={() => setModalOpen(false)}
                pageHeader='Benchmark sterkte'
                content={
                    <StrengthExplanation
                        currentScore={strength.finalScore}
                        companyCount={result.companyCount}
                        respondentCount={result.respondentCount}
                        largestShare={strength.largestShare}
                        respondentLabel={respondentLabel}
                    />
                }
            />
        </>
    )
}

export default BenchmarkHeader
